import {
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  DEFAULT_INITIAL_LEFT_WIDTH,
  DEFAULT_MIN_RIGHT_WIDTH,
  InitialLeftWidthConfig,
  KEYBOARD_RESIZE_STEP,
  RESIZE_HANDLE_WIDTH
} from './constants';
import {
  clampLeftWidth,
  getInitialLeftWidth,
  InitialLeftWidthInput,
  normalizeInitialLeftWidth
} from './utils';

export type UseResizableSplitPaneOptions = {
  initialLeftWidth?: InitialLeftWidthInput;
  minLeftWidth?: number;
  minRightWidth?: number;
  handleWidth?: number;
  onResize?: () => void;
  onResizeEnd?: () => void;
};

export type UseResizableSplitPaneResult = {
  containerRef: RefObject<HTMLDivElement>;
  isResizing: boolean;
  leftWidthPx: number | null;
  maxLeftWidthPx: number | null;
  leftPanelStyle: CSSProperties;
  rightPanelStyle: CSSProperties;
  handleMouseDown: (event: MouseEvent<HTMLDivElement>) => void;
  handleKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  adjustLeftWidthByDelta: (delta: number) => void;
  resolvedMinLeftWidth: number;
};

export const useResizableSplitPane = ({
  initialLeftWidth = DEFAULT_INITIAL_LEFT_WIDTH,
  minLeftWidth,
  minRightWidth = DEFAULT_MIN_RIGHT_WIDTH,
  handleWidth = RESIZE_HANDLE_WIDTH,
  onResize,
  onResizeEnd
}: UseResizableSplitPaneOptions = {}): UseResizableSplitPaneResult => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasUserResizedRef = useRef(false);
  const [leftWidthPx, setLeftWidthPx] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  const splitConfig: InitialLeftWidthConfig = useMemo(
    () => normalizeInitialLeftWidth(initialLeftWidth),
    [initialLeftWidth]
  );

  const resolvedMinLeftWidth = minLeftWidth ?? splitConfig.min;

  const clampWidth = useCallback(
    (leftWidth: number, containerWidth: number) =>
      clampLeftWidth({
        leftWidth,
        containerWidth,
        minLeftWidth: resolvedMinLeftWidth,
        handleWidth,
        minRightWidth
      }),
    [resolvedMinLeftWidth, handleWidth, minRightWidth]
  );

  const updateLeftWidth = useCallback(
    (containerWidth: number) => {
      if (!containerWidth) {
        return;
      }

      if (!hasUserResizedRef.current) {
        setLeftWidthPx(
          getInitialLeftWidth({
            containerWidth,
            splitConfig,
            minLeftWidth: resolvedMinLeftWidth,
            handleWidth,
            minRightWidth
          })
        );
        return;
      }

      setLeftWidthPx((currentWidth) =>
        currentWidth == null ? currentWidth : clampWidth(currentWidth, containerWidth)
      );
    },
    [splitConfig, resolvedMinLeftWidth, handleWidth, minRightWidth, clampWidth]
  );

  const syncInitialLeftWidth = useCallback(() => {
    const container = containerRef.current;
    if (!container || hasUserResizedRef.current) {
      return;
    }

    updateLeftWidth(container.getBoundingClientRect().width);
  }, [updateLeftWidth]);

  useLayoutEffect(() => {
    syncInitialLeftWidth();
  }, [syncInitialLeftWidth]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const handleContainerResize = () => {
      updateLeftWidth(container.getBoundingClientRect().width);
    };

    const resizeObserver = new ResizeObserver(handleContainerResize);
    resizeObserver.observe(container);
    window.addEventListener('resize', handleContainerResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleContainerResize);
    };
  }, [updateLeftWidth]);

  const onResizeRef = useRef(onResize);
  const onResizeEndRef = useRef(onResizeEnd);

  useEffect(() => {
    onResizeRef.current = onResize;
  }, [onResize]);

  useEffect(() => {
    onResizeEndRef.current = onResizeEnd;
  }, [onResizeEnd]);

  const adjustLeftWidthByDelta = useCallback(
    (delta: number) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      hasUserResizedRef.current = true;
      const containerWidth = container.getBoundingClientRect().width;
      setLeftWidthPx((currentWidth) => {
        const baseWidth =
          currentWidth ??
          getInitialLeftWidth({
            containerWidth,
            splitConfig,
            minLeftWidth: resolvedMinLeftWidth,
            handleWidth,
            minRightWidth
          });

        return clampWidth(baseWidth + delta, containerWidth);
      });
      onResizeRef.current?.();
    },
    [clampWidth, splitConfig, resolvedMinLeftWidth, handleWidth, minRightWidth]
  );

  const handleMouseDown = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    hasUserResizedRef.current = true;
    setIsResizing(true);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }

      event.preventDefault();
      const delta = event.key === 'ArrowLeft' ? -KEYBOARD_RESIZE_STEP : KEYBOARD_RESIZE_STEP;
      adjustLeftWidthByDelta(delta);
      onResizeEndRef.current?.();
    },
    [adjustLeftWidthByDelta]
  );

  useEffect(() => {
    if (!isResizing) {
      return undefined;
    }

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    let resizeFrameId: number | null = null;

    const scheduleResizeCallback = () => {
      if (resizeFrameId != null) {
        return;
      }

      resizeFrameId = window.requestAnimationFrame(() => {
        resizeFrameId = null;
        onResizeRef.current?.();
      });
    };

    const handleMouseMove = (event: globalThis.MouseEvent) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const newLeftWidth = event.clientX - rect.left;
      setLeftWidthPx(clampWidth(newLeftWidth, rect.width));
      scheduleResizeCallback();
    };

    const handleMouseUp = () => {
      if (resizeFrameId != null) {
        window.cancelAnimationFrame(resizeFrameId);
      }

      setIsResizing(false);
      onResizeEndRef.current?.();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      if (resizeFrameId != null) {
        window.cancelAnimationFrame(resizeFrameId);
      }

      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizing, clampWidth]);

  const leftPanelStyle = useMemo((): CSSProperties => {
    if (leftWidthPx != null) {
      return {
        width: leftWidthPx,
        minWidth: resolvedMinLeftWidth,
        maxWidth: 'none',
        flexShrink: 0
      };
    }

    if (splitConfig.percent != null) {
      return {
        width: `${splitConfig.percent * 100}%`,
        minWidth: resolvedMinLeftWidth,
        maxWidth: splitConfig.max ?? undefined,
        flexShrink: 0
      };
    }

    return {
      minWidth: resolvedMinLeftWidth,
      flexShrink: 0
    };
  }, [leftWidthPx, splitConfig, resolvedMinLeftWidth]);

  const rightPanelStyle = useMemo(
    (): CSSProperties => ({
      minWidth: minRightWidth
    }),
    [minRightWidth]
  );

  const maxLeftWidthPx = useMemo(() => {
    const container = containerRef.current;
    if (!container) {
      return null;
    }

    return Math.max(0, container.getBoundingClientRect().width - handleWidth - minRightWidth);
  }, [leftWidthPx, handleWidth, minRightWidth, isResizing]);

  return {
    containerRef,
    isResizing,
    leftWidthPx,
    maxLeftWidthPx,
    leftPanelStyle,
    rightPanelStyle,
    handleMouseDown,
    handleKeyDown,
    adjustLeftWidthByDelta,
    resolvedMinLeftWidth
  };
};
